// ============================================================================
// Hiring KPI tiles — dashboard widget (Antonela spec §3.1, Tiles A/B/C).
//
//   A. Open Hiring Requests   → /hiring?scope=all&stage=Open,Interview,On_Hold
//   B. Positions Filled (mo)  → /hiring?stage=Filled&filled=this_month
//   C. Avg Days to Fill (30d) → /hiring?stage=Filled&filled=30d
//
// Each tile shows a trend arrow vs the previous period. For Avg Days to Fill,
// a DROP is good (faster), so its arrow color is inverted.
// Gated to hr / executive / superuser by the dashboard (same as the HR inbox).
// ============================================================================

import { Link } from 'react-router-dom';
import { ArrowDownRight, ArrowUpRight, Minus } from 'lucide-react';
import { WidgetCard, WidgetSkeleton } from '../widget-card';
import { useHiringKpis } from './use-hiring-kpis';

interface Props {
  enabled: boolean;
}

export function HiringKpisWidget({ enabled }: Props) {
  const { data, isLoading, error } = useHiringKpis(enabled);
  if (!enabled) return null;

  return (
    <WidgetCard
      title="Hiring KPIs"
      subtitle="Open reqs, fills this month, and time-to-fill."
      minHeight={140}
    >
      {error && <p className="text-xs text-st-danger-fg">Couldn't load: {error}</p>}
      {!error && isLoading && <WidgetSkeleton lines={3} />}
      {!error && !isLoading && data && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <Tile
            label="Open hiring requests"
            value={data.openCount}
            delta={data.openDelta}
            deltaSuffix="vs 7d ago"
            to="/hiring?scope=all&stage=Open,Interview,On_Hold"
          />
          <Tile
            label="Filled this month"
            value={data.filledThisMonth}
            delta={data.filledDelta}
            deltaSuffix="vs last mo"
            to="/hiring?stage=Filled&filled=this_month"
          />
          <Tile
            label="Avg days to fill"
            value={data.avgDaysToFill}
            delta={data.avgDaysDelta}
            deltaSuffix="vs prior 30d"
            goodWhenDown
            to="/hiring?stage=Filled&filled=30d"
          />
        </div>
      )}
    </WidgetCard>
  );
}

function Tile({
  label,
  value,
  delta,
  deltaSuffix,
  to,
  goodWhenDown,
}: {
  label: string;
  value: number | null;
  delta: number | null;
  deltaSuffix: string;
  to: string;
  goodWhenDown?: boolean;
}) {
  return (
    <Link
      to={to}
      className="group rounded-lg border border-tcs-border bg-tcs-raised p-3 transition hover:border-tcs-border-strong hover:bg-tcs-bg-muted"
    >
      <div className="text-[11px] uppercase tracking-wide text-tcs-text-muted">
        {label}
      </div>
      <div className="mt-1 flex items-baseline gap-2">
        <span className="text-2xl font-semibold tabular-nums text-tcs-text">
          {value == null ? '—' : value}
        </span>
        {delta !== null && <TrendArrow delta={delta} suffix={deltaSuffix} goodWhenDown={goodWhenDown} />}
      </div>
    </Link>
  );
}

function TrendArrow({
  delta,
  suffix,
  goodWhenDown,
}: {
  delta: number;
  suffix: string;
  goodWhenDown?: boolean;
}) {
  const Icon = delta > 0 ? ArrowUpRight : delta < 0 ? ArrowDownRight : Minus;
  // Default: up = good (green). Inverted for metrics where lower is better.
  const positiveIsGood = !goodWhenDown;
  const good = delta === 0 ? null : delta > 0 ? positiveIsGood : !positiveIsGood;
  const color =
    good === null ? 'text-tcs-text-muted' : good ? 'text-st-ok-fg' : 'text-st-danger-fg';
  return (
    <span className={`flex items-center gap-0.5 text-[11px] ${color}`}>
      <Icon className="h-3.5 w-3.5" />
      <span className="tabular-nums">
        {delta > 0 ? '+' : ''}
        {delta}
      </span>
      <span className="text-tcs-text-muted">{suffix}</span>
    </span>
  );
}
